import { Fragment, useState } from 'react';
import { usePatchEconRowMutation } from '@shared/api';
import type { EconExtractedRow, EconSource } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { labelRowKind } from '@entities/economic/types';
import {
  EditActions,
  EditCell,
  EditError,
  EditHint,
  Muted,
  PayloadEditor,
  PayloadKey,
  PayloadList,
  PayloadVal,
  RowEditButton,
  SourceFile,
  Table,
  TableScroll,
} from './styles';
import { payloadEntries, payloadToText } from './helpers';

/** Одна извлечённая строка-источник с режимом ручной коррекции payload. Правка
 *  доступна только при `canWrite`; payload редактируется как JSON-объект
 *  (валидируется перед отправкой), backend сам снимает `needs_review`. */
export function ExtractedRow({
  row,
  sourceFilename,
  runId,
  canWrite,
}: {
  row: EconExtractedRow;
  /** Имя файла-источника строки (null — источник не найден в обработке). */
  sourceFilename: string | null;
  runId: string;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [patchRow, { isLoading: saving }] = usePatchEconRowMutation();

  function startEdit() {
    setDraft(payloadToText(row.payload));
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  // Валидность черновика: строго JSON-объект (не массив, не примитив).
  const parsed = (() => {
    const trimmed = draft.trim();
    if (!trimmed) return { ok: false as const };
    try {
      const value: unknown = JSON.parse(trimmed);
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return { ok: false as const };
      }
      // Object.keys-сужение: значение — JSON-объект, безопасно как payload.
      return { ok: true as const, value: value as Record<string, unknown> };
    } catch {
      return { ok: false as const };
    }
  })();

  async function save() {
    if (!parsed.ok) {
      setError('Данные строки должны быть валидным JSON-объектом.');
      return;
    }
    setError(null);
    try {
      await patchRow({ id: row.id, runId, payload: parsed.value }).unwrap();
      setEditing(false);
    } catch (err) {
      setError('Не удалось сохранить коррекцию. Попробуйте ещё раз.');
      // eslint-disable-next-line no-console
      console.error('patchEconRow failed', err);
    }
  }

  const entries = payloadEntries(row.payload);

  return (
    <>
      <tr>
        <td>
          {labelRowKind(row.kind)}
          {row.needs_review && (
            <>
              {' '}
              <Badge $tone="warning">проверить</Badge>
            </>
          )}
        </td>
        <td>
          {entries.length === 0 ? (
            <Muted>—</Muted>
          ) : (
            <PayloadList>
              {entries.map((e) => (
                <Fragment key={e.key}>
                  <PayloadKey>{e.label}</PayloadKey>
                  <PayloadVal>{e.value}</PayloadVal>
                </Fragment>
              ))}
            </PayloadList>
          )}
        </td>
        <td>
          {sourceFilename && <SourceFile title={sourceFilename}>{sourceFilename}</SourceFile>}
          <Muted>{row.source_ref ?? '—'}</Muted>
        </td>
        <td className="num">
          {typeof row.confidence === 'number' ? `${Math.round(row.confidence * 100)} %` : '—'}
        </td>
        <td>
          {canWrite && (
            <RowEditButton
              type="button"
              onClick={editing ? cancelEdit : startEdit}
              aria-expanded={editing}
            >
              {editing ? 'Отмена' : 'Исправить'}
            </RowEditButton>
          )}
        </td>
      </tr>
      {editing && (
        <tr>
          <EditCell colSpan={5}>
            <PayloadEditor
              $invalid={!parsed.ok}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              aria-label={`Коррекция данных строки ${row.record_uid}`}
              aria-invalid={!parsed.ok}
            />
            {error ? (
              <EditError>{error}</EditError>
            ) : (
              <EditHint>
                Отредактируйте исходные данные строки (JSON-объект). После сохранения показатели
                пересчитаются, флаг «проверить» снимется автоматически.
              </EditHint>
            )}
            <EditActions>
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                onClick={save}
                disabled={saving || !parsed.ok}
              >
                {saving ? 'Сохраняю…' : 'Сохранить коррекцию'}
              </Button>
              <Button type="button" $variant="ghost" $size="sm" onClick={cancelEdit}>
                Отмена
              </Button>
            </EditActions>
          </EditCell>
        </tr>
      )}
    </>
  );
}

export function ExtractedRows({
  rows,
  sources,
  runId,
  canWrite,
}: {
  rows: EconExtractedRow[];
  sources: EconSource[];
  runId: string;
  canWrite: boolean;
}) {
  // Имя файла-источника по source_id строки. Без него при нескольких файлах
  // в обработке (несколько КС-2 — норма) строки разных документов неотличимы,
  // и пользователь не может понять, из какого файла взято значение.
  const fileById = new Map(sources.map((s) => [s.id, s.filename]));
  if (rows.length === 0) return <Muted>Строки не извлечены.</Muted>;
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>Тип строки</th>
            <th>Извлечённые данные</th>
            <th>Где в документе</th>
            <th style={{ textAlign: 'right' }}>Уверенность</th>
            <th aria-label="Действия" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ExtractedRow
              key={row.id}
              row={row}
              sourceFilename={fileById.get(row.source_id) ?? null}
              runId={runId}
              canWrite={canWrite}
            />
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
